import { default as oneFront } from "@onefront/react-sdk";
import { default as merge } from "ts-deepmerge";

import {
    dashboardService,
    useModule,
    useGet,
    boxService as moduleService,
    launchpadMenuService,
    appSwitcherService,
    useCurrentProduct,
    useCurrentApp,
    useCurrentTenant,
    tenantSelectorService,
    useCurrentWorkspace,
    workspaceSelectorService,
    userProfileService,
    dynamicEmbedPageService,
} from "@drift/oneplatfront-components";

// Import services
import { loginPageService } from "./services/login-page";

// Import for export
import { default as boxService, OneBoxPage } from "./services/box";

import { powerBoardService } from "./services/powerboard";

export {
    useCurrentTenant,
    useCurrentProduct,
    useCurrentApp,
    useCurrentWorkspace as useCurrentCustomer,
    useCurrentWorkspace,
    useModule,
    OneBoxPage,
    useGet as useGetDigital,
};

type OneBoxParams = {
    settings?: any;
    services?: any;
    features?: any;
    [T: string]: any;
};

type StartOneBoxParams = {
    title?: string;
    appName?: string;
    appVersion?: string;
    tenantSelector?: {
        url?: string;
        redirectWhitelist?: string[];
        show?: boolean;
        priority?: number;
    };
    appsSwitcher?: {
        url?: string;
        redirectWhitelist?: string[];
        show?: boolean;
        priority?: number;
    };
    userProfile?: {
        show?: boolean;
        priority?: number;
    };
    powerboard?: {
        show?: boolean;
    };
    dynamicEmbedPage?: {
        show?: boolean;
        path?: string;
    };
    launchpad?: {
        show?: boolean;
        iframePath?: string;
    };
} & OneBoxParams;

const defaultSettings = {
    one: {
        layout: {
            showCompanySelector: false,
            appSwitch: false,
        },
    },
};

export const OneBox = {
    run: ({
        settings = {},
        services = [],
        features = [],
        ...otherConfig
    }: OneBoxParams = {}): Promise<unknown> => {
        return oneFront.run({
            ...otherConfig,
            settings: merge(defaultSettings, settings),
            features: features,
            services: [
                loginPageService,
                userProfileService,
                moduleService,
                boxService,
                tenantSelectorService,
                workspaceSelectorService,
                appSwitcherService,
                launchpadMenuService,
                dashboardService,
                powerBoardService,
                dynamicEmbedPageService,
                ...services,
            ],
        });
    },
    startApp: ({
        settings = {},
        services = [],
        features = [],
        tenantSelector = undefined,
        appsSwitcher = undefined,
        userProfile = undefined,
        powerboard = undefined,
        launchpad = undefined,
        dynamicEmbedPage = undefined,
        appName = undefined,
        appVersion = undefined,
        title = undefined,
        ...otherConfig
    }: StartOneBoxParams = {}): Promise<unknown> => {
        merge.options.allowUndefinedOverrides = false;

        /* eslint-disable indent */
        const customSettings = merge(defaultSettings, {
            one: {
                layout: title ? { title: title } : undefined,
                box: {
                    "module": {
                        name: appName,
                        version: appVersion,
                    },
                    "user-profile": userProfile
                        ? {
                              priority: userProfile?.priority,
                              show: userProfile?.show,
                          }
                        : undefined,
                    "tenant-selector": tenantSelector
                        ? {
                              priority: tenantSelector?.priority,
                              show: tenantSelector?.show,
                              urlWhitelist: tenantSelector?.redirectWhitelist,
                              tenantSelectorUrl: tenantSelector?.url,
                          }
                        : undefined,
                    "apps-switcher": appsSwitcher
                        ? {
                              priority: appsSwitcher?.priority,
                              show: appsSwitcher?.show,
                              urlWhitelist: appsSwitcher?.redirectWhitelist,
                              appsSwitcherUrl: appsSwitcher?.url,
                          }
                        : undefined,
                    "powerboard": powerboard
                        ? {
                              show: powerboard?.show,
                          }
                        : undefined,
                    "embedpage":
                        dynamicEmbedPage || launchpad
                            ? {
                                  show: dynamicEmbedPage?.show,
                                  path: dynamicEmbedPage?.path ?? launchpad?.iframePath,
                              }
                            : undefined,
                    "menu":
                        launchpad || dynamicEmbedPage
                            ? {
                                  show: launchpad?.show,
                                  iframePath: launchpad?.iframePath ?? dynamicEmbedPage?.path,
                              }
                            : undefined,
                },
            },
        });

        const finalSettings = merge(JSON.parse(JSON.stringify(customSettings)), settings);

        return oneFront.run({
            ...otherConfig,
            settings: merge(finalSettings, settings),
            features: features,
            services: [
                loginPageService,
                userProfileService,
                moduleService,
                boxService,
                tenantSelectorService,
                workspaceSelectorService,
                appSwitcherService,
                launchpadMenuService,
                dashboardService,
                powerBoardService,
                dynamicEmbedPageService,
                ...services,
            ],
        });
    },
};

export default OneBox;
